import { useMemo } from "react";
import { ExternalLink, Landmark } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";

// ─── Standard Color Config ───────────────────────────────────────────────────

export const CLAUSES_COLORS = {
  bar: "#E8862A",
  bg: "rgba(232,134,42,0.08)",
  text: "#E8862A",
};

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ClauseInfo {
  standard: string;
  title: string;
  description: string;
}

export interface ClauseInfoWithRef extends ClauseInfo {
  ref: string;
}

// ─── Clause Reference Badge ─────────────────────────────────────────────────

export function ClauseRefBadge({
  clauseRef,
  clauseInfo,
}: {
  clauseRef: string;
  clauseInfo?: ClauseInfo;
}) {
  const colors = CLAUSES_COLORS;

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          data-clause-ref=""
          className="group/clause inline-flex items-center gap-1.5 rounded-[3px] py-[3px] pl-[7px] pr-2 text-[11px] transition-all duration-150 cursor-pointer border-l-[2.5px]"
          style={{
            borderLeftColor: colors.bar,
            backgroundColor: colors.bg,
            color: colors.text,
          }}
        >
          <span
            className="font-mono font-semibold tracking-tight whitespace-nowrap"
            style={{ fontSize: "10.5px" }}
          >
            {clauseRef}
          </span>
          <ExternalLink
            size={9}
            className="opacity-0 group-hover/clause:opacity-70 transition-opacity shrink-0"
            style={{ color: colors.text }}
          />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={6}
          className="z-50 w-[320px] overflow-hidden rounded-[10px] bg-card border border-border shadow-xl"
        >
          {/* Accent top bar */}
          <div className="h-[3px]" style={{ backgroundColor: colors.bar }} />

          <div className="p-4">
            {clauseInfo ? (
              <div className="space-y-2.5">
                <div className="flex items-center gap-2">
                  <Landmark
                    size={13}
                    style={{ color: colors.text }}
                    className="shrink-0"
                  />
                  <span
                    className="text-[10px] font-semibold uppercase tracking-[0.08em]"
                    style={{ color: colors.text }}
                  >
                    {clauseInfo.standard}
                  </span>
                </div>

                <div className="space-y-1">
                  <p className="text-[13px] font-semibold text-primary leading-snug">
                    {clauseInfo.title}
                  </p>
                  <p
                    className="font-mono text-[10px] tracking-wide"
                    style={{ color: colors.text, opacity: 0.7 }}
                  >
                    {clauseRef}
                  </p>
                </div>

                <div className="h-px bg-border" />

                <p className="text-[12px] text-secondary leading-relaxed">
                  {clauseInfo.description}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Landmark size={13} className="text-muted shrink-0" />
                  <span className="font-mono text-[12px] font-semibold text-primary">
                    {clauseRef}
                  </span>
                </div>
                <p className="text-[12px] text-muted">
                  No additional details available for this clause reference.
                </p>
              </div>
            )}
          </div>
          <Popover.Arrow className="fill-card" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// ─── Helper ──────────────────────────────────────────────────────────────────

function getStandardAbbreviation(standard: string): string {
  if (standard.includes("Sarbanes")) return "SOX";
  if (standard.includes("PCAOB")) return "PCAOB";
  if (standard.includes("ASC 606")) return "ASC 606";
  if (standard.includes("ASC 810")) return "ASC 810";
  if (standard.includes("COSO")) return "COSO";
  if (standard.includes("COBIT")) return "COBIT";
  return standard.slice(0, 5).toUpperCase();
}

// ─── Grouped Clause References ──────────────────────────────────────────────

export function ClauseRefsGroup({
  clauseRefs,
  clauseInfo,
}: {
  clauseRefs: string[];
  clauseInfo?: ClauseInfoWithRef[];
}) {
  const groups = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const ref of clauseRefs) {
      const info = clauseInfo?.find((ci) => ci.ref === ref);
      const standard = info?.standard ?? "Other";
      const existing = map.get(standard);
      if (existing) {
        existing.push(ref);
      } else {
        map.set(standard, [ref]);
      }
    }
    return Array.from(map.entries());
  }, [clauseRefs, clauseInfo]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 mb-1">
        <Landmark size={12} className="text-muted" />
        <span className="text-[10px] font-semibold text-muted uppercase tracking-[0.06em]">
          Regulatory References
        </span>
      </div>
      {groups.map(([standard, refs]) => {
        const colors = CLAUSES_COLORS;
        return (
          <div key={standard} className="flex items-start gap-2">
            <span
              className="text-[9px] font-semibold uppercase tracking-wide mt-[5px] shrink-0 w-[44px] text-right leading-tight"
              style={{ color: colors.text, opacity: 0.6 }}
            >
              {getStandardAbbreviation(standard)}
            </span>
            <div className="flex flex-wrap gap-1">
              {refs.map((ref) => (
                <ClauseRefBadge
                  key={ref}
                  clauseRef={ref}
                  clauseInfo={clauseInfo?.find((ci) => ci.ref === ref)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
